import {
  ActionIcon,
  Box,
  Center,
  Group,
  Menu,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconCheck,
  IconClockHour3,
  IconDotsVertical,
  IconEyeClosed,
  IconImageInPicture,
  IconMessageExclamation,
  IconPencil,
  IconSearch,
  IconTrendingUp,
  IconTrophy,
  IconX,
} from "@tabler/icons-react";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import { safeApiCall } from "api/common.service";
import { toggleState } from "api/product.service";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import EditProductModal from "components/products/EditProductModal";
import UploadImagesModal from "components/products/UploadImagesModal";
import { useProductStore } from "stores/useProductStore"; // adjust path as needed
import { ProductSummary } from "types/model.types";

const ManageProductPage: React.FC = observer(() => {
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [imageModelOpened, setImageModalOpened] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductSummary | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const products = useProductStore((s) => s.allProducts.items);
  const syncProducts = useProductStore((s) => s.syncModifiedProducts);

  const syncLocalProductChanges = useProductStore(
    (s) => s.syncLocalProductChanges,
  );
  const [activePage, setActivePage] = useState(1);
  const { brands, categories } = useProductStore();

  const filteredItems = useMemo(() => {
    return products.filter(
      (p: ProductSummary) =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.productCode ?? "")
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );
  }, [products, searchTerm]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  const resolveNames = (
    idsCsv: string,
    lookup: { id: string | number; name: string }[],
  ) => {
    const maxLength = 18;
    const ids = idsCsv?.split(",").map((id) => id.trim().toLowerCase()) ?? [];
    const names = lookup
      .filter((item) => ids.includes(item.id.toString().toLowerCase()))
      .map((item) => item.name)
      .join(", ");

    return names.length > maxLength
      ? names.slice(0, maxLength).trimEnd() + "…"
      : names;
  };
  const rows = paginatedItems.map((product) => (
    <Table.Tr key={product.id}>
      <Table.Td>{product.productCode}</Table.Td>
      <Table.Td>
        <Stack gap={1} align="left" mt="xs">
          {product.title}
          <Group gap="xs">
            {product.shortDated ? (
              <Text size="xs" c="yellow.7">
                <IconClockHour3 size={14} /> Short Dated
              </Text>
            ) : (
              <Text size="xs" c="gray.6">
                <IconClockHour3 size={14} /> Short Dated
              </Text>
            )}
            {product.onRequest ? (
              <Text size="xs" c="blue.6">
                <IconMessageExclamation size={14} /> On Request
              </Text>
            ) : (
              <Text size="xs" c="gray.6">
                <IconMessageExclamation size={14} /> On Request
              </Text>
            )}
            {product.topSelling ? (
              <Text size="xs" c="red.6">
                <IconTrendingUp size={14} /> Top Seller
              </Text>
            ) : (
              <Text size="xs" c="gray.6">
                <IconTrendingUp size={14} /> Top Seller
              </Text>
            )}
            {product.featured ? (
              <Text size="xs" c="green.6">
                <IconTrophy size={14} /> Featured
              </Text>
            ) : (
              <Text size="xs" c="gray.6">
                <IconTrophy size={14} /> Featured
              </Text>
            )}
          </Group>
        </Stack>
      </Table.Td>
      <Table.Td>{resolveNames(product.brandIds, brands)}</Table.Td>
      <Table.Td>{resolveNames(product.categoryIds, categories)}</Table.Td>
      <Table.Td>{product.sellPrice2.toFixed(2)}</Table.Td>
      <Table.Td>{product.unitsCtn}</Table.Td>
      <Table.Td>{product.barcode}</Table.Td>
      <Table.Td>
        <Menu shadow="md" width={180}>
          <Menu.Target>
            <ActionIcon variant="subtle">
              <IconDotsVertical />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconPencil size={16} />}
              onClick={() => {
                setSelectedProduct(product); // assuming `product` is scoped here
                setImageModalOpened(false);
                setEditModalOpened(true);
              }}
            >
              Edit Product
            </Menu.Item>
            <Menu.Item
              leftSection={<IconImageInPicture size={16} />}
              onClick={() => {
                setSelectedProduct(product); // assuming `product` is
                setImageModalOpened(true);
                setEditModalOpened(false);
              }}
            >
              Upload images
            </Menu.Item>
            <Menu.Item
              leftSection={
                product.featured ? <IconX size={16} /> : <IconCheck size={16} />
              }
              onClick={async () => {
                //
                await safeApiCall(
                  toggleState(
                    product.id,
                    true, // featured
                    undefined,
                    undefined,
                    undefined,
                  ),
                );
                useProductStore.setState((state) => ({
                  allProducts: {
                    ...state.allProducts,
                    items: state.allProducts.items.map((p) =>
                      p.id === product.id ? { ...p, featured: !p.featured } : p,
                    ),
                  },
                }));
              }}
            >
              Toggle featured
            </Menu.Item>

            <Menu.Item
              leftSection={<IconEyeClosed size={16} />}
              onClick={async () => {
                //
                await safeApiCall(
                  toggleState(
                    product.id,
                    undefined,
                    true, // closed
                    undefined,
                    undefined,
                  ),
                );
              }}
            >
              Toggle closed
            </Menu.Item>
            <Menu.Item
              leftSection={<IconMessageExclamation size={16} />}
              onClick={async () => {
                //
                await safeApiCall(
                  toggleState(
                    product.id,
                    undefined,
                    undefined,
                    true, // onRequest
                    undefined,
                  ),
                );
                useProductStore.setState((state) => ({
                  allProducts: {
                    ...state.allProducts,
                    items: state.allProducts.items.map((p) =>
                      p.id === product.id
                        ? { ...p, onRequest: !p.onRequest }
                        : p,
                    ),
                  },
                }));
              }}
            >
              Toggle on request
            </Menu.Item>
            <Menu.Item
              leftSection={<IconClockHour3 size={16} />}
              onClick={async () => {
                //
                await safeApiCall(
                  toggleState(
                    product.id,
                    undefined,
                    undefined,
                    undefined,
                    true, // shortDated
                  ),
                );
                useProductStore.setState((state) => ({
                  allProducts: {
                    ...state.allProducts,
                    items: state.allProducts.items.map((p) =>
                      p.id === product.id
                        ? { ...p, shortDated: !p.shortDated }
                        : p,
                    ),
                  },
                }));
              }}
            >
              Toggle short dated
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  ));
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
    setActivePage(1); // resets to first page for new search
  };

  return (
    <Box>
      <Stack>
        <Title order={2}>Manage Products</Title>
        <Text size="sm" c="dimmed">
          Showing {DEFAULT_PAGE_SIZE} items per page
        </Text>
        <TextInput
          placeholder="Search by name"
          mb="md"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Table highlightOnHover striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Code</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Brand</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>SP 2</Table.Th>
              <Table.Th>Units/CTN</Table.Th>
              <Table.Th>Barcode</Table.Th>
              <Table.Th>Action</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>{rows}</Table.Tbody>

          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={9}>
                <Center mt="md">
                  <Pagination
                    value={activePage}
                    onChange={setActivePage}
                    total={Math.ceil(filteredItems.length / DEFAULT_PAGE_SIZE)}
                  />
                </Center>
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Stack>

      {selectedProduct && (
        <EditProductModal
          opened={editModalOpened}
          onClose={() => setEditModalOpened(false)}
          product={selectedProduct}
          onSave={(updated: ProductSummary) => {
            syncLocalProductChanges(updated);
          }}
        />
      )}
      {selectedProduct && (
        <UploadImagesModal
          opened={imageModelOpened}
          onClose={() => setImageModalOpened(false)}
          product={selectedProduct}
          onSave={() => {
            //
            //syncProducts(); // force refresh - this will fetch modified records from db and sync with local collection
          }}
        />
      )}
    </Box>
  );
});

export default ManageProductPage;
